import {
  Badge, Button, Container, Group, Pagination, Stack, Table, Text, Title,
  Tooltip,
} from '@mantine/core';
import { IconDownload, IconFilterOff } from '@tabler/icons-react';
import { useState } from 'react';

import type { AuditAction, AuditLogEntry, AuditSource } from '../api/types';
import { useAudit } from '../hooks/useAudit';
import { useCurrentUser } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useStaffOptions } from '../hooks/useAgreementInteractions';
import {
  ColumnDateRangeFilter, ColumnMultiSelectFilter, ColumnTextFilter,
  FilteredColumnLabel, hasActiveFilters,
} from '../components/ColumnFilters';
import { BackToCompliance } from '../components/BackToCompliance';
import { pepLabel, usPersonLabel } from '../utils/pepLabels';
import { commsValueLabel, isCommsField } from '../utils/commsPreferences';

const PAGE_SIZE = 50;

// #47 follow-up (2026-06-12, Pedro): the audit log adopts the same
// header-filter pattern (C-132) as the entity lists. Closed sets are
// multi-select; the Actor picker is the staff people-picker (multi,
// C-132 Amendment 3) and is shown to admins only — non-admins are
// server-scoped to their own rows anyway.
const ACTION_OPTS = [
  { value: 'INSERT', label: 'INSERT' },
  { value: 'UPDATE', label: 'UPDATE' },
  { value: 'SOFT_DELETE', label: 'SOFT_DELETE' },
  { value: 'RESTORE', label: 'RESTORE' },
  { value: 'HARD_DELETE', label: 'HARD_DELETE' },
];

const SOURCE_OPTS = [
  { value: 'UI', label: 'UI' },
  { value: 'API', label: 'API' },
  { value: 'MIGRATION', label: 'MIGRATION' },
  { value: 'SYSTEM', label: 'SYSTEM' },
];

const ACTION_COLOURS: Record<string, string> = {
  INSERT: 'green', UPDATE: 'blue', SOFT_DELETE: 'orange',
  RESTORE: 'cyan', HARD_DELETE: 'red',
};

/**
 * Truncate any JSON value to a short, readable string for the audit
 * Diff column. Strings get quoted and elided past 40 chars; nulls
 * render as a dimmed em-dash; objects/arrays get their JSON form
 * trimmed. The point is to keep the column scannable without losing
 * the *kind* of value that changed.
 */
function _formatValue(v: unknown, key?: string): string {
  if (v === null || v === undefined) return '∅';
  // P4 (2026-10-09): PEP / US person also read in English (stored value kept).
  if (key === 'pep_status' && typeof v === 'string' && pepLabel(v) !== v) {
    return `"${v}" (${pepLabel(v)})`;
  }
  if (key === 'us_person' && typeof v === 'boolean') {
    return `${v} (${usPersonLabel(v)})`;
  }
  // P13: email preferences.
  if (isCommsField(key) && typeof v === 'boolean') {
    return `${v} (${commsValueLabel(v)})`;
  }
  if (typeof v === 'string') {
    return v.length > 40 ? `"${v.slice(0, 37)}…"` : `"${v}"`;
  }
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 40 ? `${s.slice(0, 37)}…` : s;
  } catch {
    return String(v);
  }
}

/**
 * Render the Diff column for one audit row.
 *
 * Priority order:
 *   1. UPDATE / RESTORE / SOFT_DELETE rows with diff_keys present →
 *      show "field: old → new" lines for each changed key.
 *   2. data_extraction_signal rows → show event_type from
 *      new_state.event_type.
 *   3. Rows with notes → show notes.
 *   4. Otherwise → em-dash.
 *
 * Pedro 2026-05-04 round-2: previous version showed only the comma-
 * separated diff_keys, which felt incomplete. Now each changed field
 * carries its before/after values inline, parsed from the captured
 * old_state / new_state JSON columns.
 */
function DiffCell({ row }: { row: AuditLogEntry }) {
  if (row.diff_keys && row.diff_keys.length > 0) {
    const oldState = (row.old_state ?? {}) as Record<string, unknown>;
    const newState = (row.new_state ?? {}) as Record<string, unknown>;
    return (
      <Stack gap={2}>
        {/* Pedro 2026-05-04 round-5 follow-up: previously
            whiteSpace: 'nowrap' was set on each diff line, which
            forced the browser to expand the Diff column to fit the
            longest line. Combined with the new tableLayout: 'fixed'
            on the parent table, that no longer works — and we'd
            rather have wrapping than horizontal overflow. Switched to
            wordBreak: 'break-word' so long timestamp / UUID values
            wrap to a new line within the same diff entry. */}
        {row.diff_keys.map((k) => (
          <Text key={k} size="xs" ff="monospace"
            style={{ wordBreak: 'break-word' }}>
            <Text span c="dimmed">{k}: </Text>
            <Text span>{_formatValue(oldState[k], k)}</Text>
            <Text span c="dimmed"> → </Text>
            <Text span fw={600}>{_formatValue(newState[k], k)}</Text>
          </Text>
        ))}
      </Stack>
    );
  }
  if (row.entity_type === 'data_extraction_signal'
      && row.new_state
      && typeof (row.new_state as Record<string, unknown>).event_type === 'string') {
    return <Text size="xs">{(row.new_state as Record<string, unknown>).event_type as string}</Text>;
  }
  // AUD-JR (Pedro 2026-10-26, option (b)): a replace-style line (bucket member, model line, view
  // lens, …) is deleted and re-inserted on every save of its record; say so, with its values.
  if (row.replace_note) {
    return <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>{row.replace_note}</Text>;
  }
  if (row.notes) {
    return <Text size="xs" c="dimmed">{row.notes}</Text>;
  }
  return <Text size="xs" c="dimmed">—</Text>;
}

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [actionF, setActionF] = useState<string[]>([]);
  const [sourceF, setSourceF] = useState<string[]>([]);
  const [actorF, setActorF] = useState<string[]>([]);
  const [tsFrom, setTsFrom] = useState('');
  const [tsTo, setTsTo] = useState('');
  const { user: currentUser } = useCurrentUser();
  const { can } = usePermissions();
  const fullTrail = currentUser?.role === 'admin' || currentUser?.role === 'compliance';

  const filtersActive = hasActiveFilters({
    entityType, entityId, actionF, sourceF, actorF, tsFrom, tsTo,
  });
  function clearFilters() {
    setEntityType(''); setEntityId(''); setActionF([]); setSourceF([]);
    setActorF([]); setTsFrom(''); setTsTo('');
    setPage(1);
  }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const staffQ = useStaffOptions();
  const actorOptions = (Array.isArray(staffQ.data) ? staffQ.data : [])
    .map((u) => ({ value: u.id, label: u.display_name }));

  const { data, isLoading, error } = useAudit({
    entity_type: entityType || undefined,
    entity_id: entityId || undefined,
    action: actionF.length > 0 ? (actionF as AuditAction[]) : undefined,
    source: sourceF.length > 0 ? (sourceF as AuditSource[]) : undefined,
    actor_user_id: actorF.length > 0 ? actorF : undefined,
    ts_from: tsFrom || undefined,
    ts_to: tsTo || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // Phase H0 #109: admin-only CSV export. Click triggers a same-origin
  // navigation to /audit/export.csv with the currently-applied filters
  // as query string (arrays expand to repeated keys), so the CSV always
  // matches the screen (§3j). The backend's streaming response writes a
  // self-audit row before yielding bytes.
  function downloadExportUrl(): string {
    const params = new URLSearchParams();
    if (entityType) params.set('entity_type', entityType);
    if (entityId) params.set('entity_id', entityId);
    for (const a of actionF) params.append('action', a);
    for (const s of sourceF) params.append('source', s);
    for (const u of actorF) params.append('actor_user_id', u);
    if (tsFrom) params.set('ts_from', tsFrom);
    if (tsTo) params.set('ts_to', tsTo);
    const qs = params.toString();
    return `/audit/export.csv${qs ? '?' + qs : ''}`;
  }

  return (
    <Container size="xl">
      <Stack>
        <BackToCompliance />
        <Group justify="space-between">
          <Title order={2}>Audit log</Title>
          {can('audit.export') && (
            <Button
              component="a"
              href={downloadExportUrl()}
              leftSection={<IconDownload size={16} />}
              variant="default"
            >
              Export CSV
            </Button>
          )}
        </Group>

        {error && <Text c="red">{error.detail}</Text>}

        {/* Pedro 2026-05-04 round-5 follow-up: tableLayout: 'fixed' is
            required for Mantine's Table.Th width hints to be honoured
            by the browser. Without it, a column with long content (here:
            Diff) consumes the slack and squishes the others — that's
            why the Action column was rendering "I…" / "S…" instead of
            "INSERT" / "SOFT_DELETE" badges. With tableLayout fixed, the
            Diff column gets only the remaining space and its content
            wraps inside the cell instead of expanding the column.
            Action width bumped to 130 so the longest action label
            ("HARD_DELETE") fits comfortably. */}
        <Table withTableBorder striped style={{ tableLayout: 'fixed' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={170}>
                <FilteredColumnLabel active={tsFrom !== '' || tsTo !== ''}>Timestamp</FilteredColumnLabel>
              </Table.Th>
              <Table.Th w={130}>
                <FilteredColumnLabel active={actionF.length > 0}>Action</FilteredColumnLabel>
              </Table.Th>
              {/* Entity column was 140 — too narrow for
                  "insurance_policy_accounts" (25 chars). Bumped to 200
                  so the longest current entity_type fits without
                  wrapping. */}
              <Table.Th w={200}>
                <FilteredColumnLabel active={entityType !== ''}>Entity</FilteredColumnLabel>
              </Table.Th>
              <Table.Th w={220}>
                <FilteredColumnLabel active={entityId !== ''}>Entity ID</FilteredColumnLabel>
              </Table.Th>
              <Table.Th w={200}>
                <FilteredColumnLabel active={actorF.length > 0}>Actor</FilteredColumnLabel>
              </Table.Th>
              <Table.Th w={90}>
                <FilteredColumnLabel active={sourceF.length > 0}>Source</FilteredColumnLabel>
              </Table.Th>
              <Table.Th>Diff</Table.Th>
            </Table.Tr>
            {/* #47 follow-up (2026-06-12): per-column filter row (C-132),
                replacing the old top filter bar. */}
            <Table.Tr>
              <Table.Th>
                <ColumnDateRangeFilter from={tsFrom} to={tsTo}
                  onChange={(f, t) => { setTsFrom(f); setTsTo(t); setPage(1); }}
                  ariaLabelPrefix="Timestamp" />
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={actionF} onChange={paged(setActionF)}
                  options={ACTION_OPTS} allLabel="All" ariaLabel="Filter by action" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={entityType} onChange={paged(setEntityType)}
                  placeholder="e.g. persons" ariaLabel="Filter by entity type" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={entityId} onChange={paged(setEntityId)}
                  placeholder="Entity ID (UUID)" ariaLabel="Filter by entity ID" />
              </Table.Th>
              <Table.Th>
                {/* Only Admin and Compliance see everyone's rows (option B′, C-358);
                    others are server-scoped to their own — the actor picker would be a
                    dead control for them. */}
                {fullTrail ? (
                  <ColumnMultiSelectFilter values={actorF} onChange={paged(setActorF)}
                    options={actorOptions} allLabel="Anyone" ariaLabel="Filter by actor" />
                ) : null}
              </Table.Th>
              <Table.Th>
                <ColumnMultiSelectFilter values={sourceF} onChange={paged(setSourceF)}
                  options={SOURCE_OPTS} allLabel="All" ariaLabel="Filter by source" />
              </Table.Th>
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && <Table.Tr><Table.Td colSpan={7}><Text c="dimmed">Loading…</Text></Table.Td></Table.Tr>}
            {!isLoading && data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  {filtersActive ? (
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No audit rows match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  ) : (
                    <Text c="dimmed">No audit rows.</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {data?.items.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td><Text size="xs" ff="monospace">{new Date(row.ts).toLocaleString()}</Text></Table.Td>
                <Table.Td>
                  <Badge variant="light" color={ACTION_COLOURS[row.action]}>{row.action}</Badge>
                </Table.Td>
                <Table.Td>{row.entity_type}</Table.Td>
                <Table.Td>
                  {/* Entity ID — Pedro 2026-05-04 round-4 (polish):
                      backend-resolved `friendly_label` is the source of
                      truth. Display rules:
                        • show the friendly label as the primary line
                        • for table-backed UUID entity_types only
                          (users / agreements / persons / accounts /
                          insurance_policies) show the first 8 chars of
                          the UUID as a 10px dimmed suffix, so an admin
                          can still match across rows for the same record
                        • for audit_export / data_extraction_signal,
                          suppress the UUID suffix entirely — the friendly
                          label (timestamp, event_type) IS the meaningful
                          identifier; the UUID prefix in legacy rows is
                          noise
                        • when friendly_label is null (unknown
                          entity_type, orphaned record), render the raw
                          entity_id but truncate past 36 chars with the
                          full value available on hover, so the column
                          doesn't blow out for legacy long strings. */}
                  {(() => {
                    const fl = row.friendly_label;
                    const eid = row.entity_id;
                    const isUuid = /^[0-9a-f-]{36}$/i.test(eid);
                    const isSpecial = row.entity_type === 'audit_export'
                                   || row.entity_type === 'data_extraction_signal';
                    if (fl) {
                      return (
                        <Stack gap={0}>
                          <Text size="xs">{fl}</Text>
                          {!isSpecial && isUuid && eid !== fl && (
                            <Text size="10px" ff="monospace" c="dimmed">
                              {eid.slice(0, 8)}…
                            </Text>
                          )}
                        </Stack>
                      );
                    }
                    // No friendly_label resolved. Pedro 2026-05-04
                    // round-5 fixup: previous truncation only fired
                    // past 36 chars, leaving full standard UUIDs (36
                    // chars exactly) intact. Now a UUID-shaped
                    // entity_id renders as the 8-char prefix + … so
                    // the column never blows out for entity types
                    // we haven't added a labeler for yet. Full value
                    // remains available on hover via title attr.
                    let truncated = eid;
                    if (isUuid) {
                      truncated = `${eid.slice(0, 8)}…`;
                    } else if (eid.length > 36) {
                      truncated = `${eid.slice(0, 33)}…`;
                    }
                    return (
                      <Text size="xs" ff="monospace" c="dimmed" title={eid}>
                        {truncated}
                      </Text>
                    );
                  })()}
                </Table.Td>
                <Table.Td>
                  {/* Pedro 2026-05-04 round-5 fixup: previously the
                      Actor column fell straight from actor_label
                      (often NULL on trigger-driven rows) to an 8-char
                      UUID prefix. The backend now also resolves the
                      user's CURRENT email at read-time as a fallback,
                      so the column shows a human identity even when
                      the trigger captured no actor_label. Order:
                        1. actor_label (immutable, captured at write)
                        2. actor_email_resolved (lookup at read time)
                        3. UUID prefix (orphaned actor — user gone)
                        4. em-dash (no actor at all — system action) */}
                  {row.actor_label
                    ? <Text size="xs">{row.actor_label}</Text>
                    : row.actor_email_resolved
                      ? <Text size="xs">{row.actor_email_resolved}</Text>
                      : row.actor_user_id
                        ? <Text size="xs" ff="monospace" c="dimmed">{row.actor_user_id.slice(0, 8)}…</Text>
                        : <Text size="xs" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td><Text size="xs">{row.source}</Text></Table.Td>
                <Table.Td><DiffCell row={row} /></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} entries</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}
